import { parseIntStrict } from '@/lib/utils';

function parseEquation(str: unknown): { left: number; right: number } | null {
    if (!str) return null;
    const s = String(str).trim();
    const m = s.match(/^([+\-]?\d+)\s*[\/÷:]\s*([+\-]?\d+)$/);
    if (!m) return null;
    return { left: parseIntStrict(m[1]), right: parseIntStrict(m[2]) };
}

const STEP_TYPES = {
    CHECK: 'check',
    DIVIDE: 'divide',
} as const;

const LAYOUT = {
    padding: 16,
    digitWidth: 22,
    fontSize: 16,
    topSpace: 56,
    bottomSpace: 56,
    maxDPR: 3,
    barOffset: 6,
    lineWidth: 2,
    strikeLineWidth: 1.6,
    carryFontSize: 9,
};

const COLORS = {
    text: '#0f172a',
    muted: '#667085',
    line: '#e3e8ef',
    accent: '#2563eb',
    danger: '#dc2626',
    background: '#ffffff',
};

interface CheckStep {
    type: typeof STEP_TYPES.CHECK;
    startIndex: number;
    endIndex: number;
    quotientDigit: number;
    before: number;
}

interface DivideStep {
    type: typeof STEP_TYPES.DIVIDE;
    startIndex: number;
    endIndex: number;
    quotientDigit: number;
    divisorProduct: number;
    before: number;
    remainder: number;
}

type BusStopStep = CheckStep | DivideStep;

export interface BusStopSolution {
    quotient: number;
    remainder: number;
    steps: BusStopStep[];
    quotientSlots: (number | undefined)[];
    dividend: number;
    divisor: number;
    signQ: number;
}

export interface BusStopError {
    error: string;
}

export type BusStopResult = BusStopSolution | BusStopError;

interface DrawOptions {
    upToStep?: number;
    showSummary?: boolean;
}

function digitsOf(number: number): number[] {
    return String(Math.abs(number))
        .split('')
        .map((digit) => Number(digit));
}

function findLastDivideStep(steps: BusStopStep[], beforeIndex: number): DivideStep | null {
    for (let stepIndex = beforeIndex - 1; stepIndex >= 0; stepIndex--) {
        if (steps[stepIndex].type === STEP_TYPES.DIVIDE) {
            return steps[stepIndex] as DivideStep;
        }
    }
    return null;
}

function computeLongDivision(dividend: number, divisor: number): BusStopResult {
    const quotientSign = dividend < 0 !== divisor < 0 ? -1 : 1;
    const absoluteDividend = Math.abs(dividend);
    const absoluteDivisor = Math.abs(divisor);

    if (absoluteDivisor === 0) {
        return { error: 'Division by zero is undefined.' };
    }

    const dividendDigits = digitsOf(absoluteDividend);
    const dividendDigitCount = dividendDigits.length;
    let currentValue = 0;
    let hasStarted = false;
    const quotientSlots: (number | undefined)[] = new Array(dividendDigitCount).fill(undefined);
    const steps: BusStopStep[] = [];

    for (let i = 0; i < dividendDigitCount; i++) {
        currentValue = currentValue * 10 + dividendDigits[i];
        const valueBeforeOperation = currentValue;

        const previousStepEndIndex = steps.length > 0 ? steps[steps.length - 1].endIndex : -1;
        const startIndex = hasStarted ? previousStepEndIndex + 1 : 0;

        if (currentValue < absoluteDivisor) {
            steps.push({
                type: STEP_TYPES.CHECK,
                startIndex,
                endIndex: i,
                quotientDigit: 0,
                before: valueBeforeOperation,
            });
            quotientSlots[i] = 0;
            continue;
        }

        hasStarted = true;
        const quotientDigit = Math.floor(currentValue / absoluteDivisor);
        const divisorProduct = quotientDigit * absoluteDivisor;
        const remainder = currentValue - divisorProduct;

        steps.push({
            type: STEP_TYPES.DIVIDE,
            startIndex,
            endIndex: i,
            quotientDigit,
            divisorProduct,
            before: valueBeforeOperation,
            remainder,
        });
        quotientSlots[i] = quotientDigit;
        currentValue = remainder;
    }

    const quotientString = quotientSlots.filter((d) => d !== undefined).join('');
    const absoluteQuotient = quotientString === '' ? 0 : Number(quotientString);
    const quotient = quotientSign < 0 ? -absoluteQuotient : absoluteQuotient;
    const finalRemainder = dividend < 0 ? -currentValue : currentValue;

    return {
        quotient,
        remainder: finalRemainder,
        steps,
        quotientSlots,
        dividend: absoluteDividend,
        divisor: absoluteDivisor,
        signQ: quotientSign,
    };
}

export function buildStepText(
    solution: BusStopResult | null,
    upToStep: number | null = null,
): string {
    if (!solution) return '';
    if ('error' in solution) return solution.error;

    const steps = solution.steps || [];
    if (upToStep == null) upToStep = steps.length - 1;

    const maxStep = Math.min(steps.length - 1, Math.max(0, upToStep));
    const lines: string[] = [];
    const dividendStr = String(solution.dividend);
    const digitCount = dividendStr.length;
    const firstDivideIndex = steps.findIndex((st) => st.type === STEP_TYPES.DIVIDE);

    lines.push(`We are solving: ${solution.dividend} ÷ ${solution.divisor}`);

    for (let stepIndex = 0; stepIndex <= maxStep; stepIndex++) {
        const step = steps[stepIndex];
        const currentDividendPart = step.before;
        lines.push(`Step ${stepIndex + 1}:`);

        switch (step.type) {
            case STEP_TYPES.CHECK:
                handleCheckStep(
                    lines,
                    steps,
                    stepIndex,
                    step,
                    currentDividendPart,
                    dividendStr,
                    digitCount,
                    solution.divisor,
                );
                break;

            case STEP_TYPES.DIVIDE:
                handleDivideStep(
                    lines,
                    steps,
                    stepIndex,
                    step,
                    currentDividendPart,
                    dividendStr,
                    firstDivideIndex,
                    solution.divisor,
                );
                break;

            default:
                console.warn(`Unknown step type: ${(step as BusStopStep).type}`);
        }
    }

    if (maxStep === steps.length - 1) {
        if (solution.remainder) {
            lines.push(`Remainder is ${Math.abs(solution.remainder)}.`);
        } else {
            lines.push('No remainder.');
        }
        lines.push(
            `Answer: ${solution.quotient}` +
                (solution.remainder ? ` r ${Math.abs(solution.remainder)}` : ''),
        );
    }

    return lines.join('\n');
}

function handleCheckStep(
    lines: string[],
    steps: BusStopStep[],
    stepIndex: number,
    step: CheckStep,
    currentDividendPart: number,
    dividendStr: string,
    digitCount: number,
    divisor: number,
): void {
    if (stepIndex === 0) {
        lines.push(`- Look at ${currentDividendPart}.`);
    } else {
        lines.push(`- ${getCarryDescription(steps, stepIndex, dividendStr)}`);
    }

    lines.push(`- Does ${divisor} fit into ${currentDividendPart}? No.`);

    if (step.endIndex < digitCount - 1) {
        lines.push(
            `- Write 0 above this number and look at the next number (which is ${dividendStr[step.endIndex + 1]}).`,
        );
    } else {
        lines.push(`- Write 0 above this number. There are no more digits to include.`);
    }
}

function handleDivideStep(
    lines: string[],
    steps: BusStopStep[],
    stepIndex: number,
    step: DivideStep,
    currentDividendPart: number,
    dividendStr: string,
    firstDivideIndex: number,
    divisor: number,
): void {
    if (stepIndex === 0 || stepIndex === firstDivideIndex) {
        const digitCountUsed = step.endIndex + 1;
        const plural = digitCountUsed === 1 ? '' : 's';
        if (step.endIndex > 0) {
            lines.push(
                `- Use the first ${digitCountUsed} digit${plural} of the dividend to get ${currentDividendPart}.`,
            );
        } else {
            lines.push(`- Start with ${currentDividendPart}.`);
        }
    } else {
        lines.push(`- ${getCarryDescription(steps, stepIndex, dividendStr)}`);
    }

    const { quotientDigit, divisorProduct, remainder } = step;

    lines.push(
        `- How many times does ${divisor} fit in ${currentDividendPart}? ${quotientDigit} time(s).`,
    );
    lines.push(`- Multiply: ${quotientDigit} × ${divisor} = ${divisorProduct}.`);
    lines.push(
        `- Subtract: ${currentDividendPart} − ${divisorProduct} = ${remainder}. This leaves a remainder of ${remainder}.`,
    );
}

function getCarryDescription(
    steps: BusStopStep[],
    currentIndex: number,
    dividendStr: string,
): string {
    const previousStep = steps[currentIndex - 1];
    const currentStep = steps[currentIndex];
    const lastDivideStep = findLastDivideStep(steps, currentIndex);
    const nextDigit = dividendStr[currentStep.endIndex];
    const currentDividendPart = currentStep.before;

    switch (previousStep.type) {
        case STEP_TYPES.DIVIDE: {
            const previousRemainder = lastDivideStep ? lastDivideStep.remainder : 0;
            return `Put the remainder ${previousRemainder} next to the next digit from the dividend (which is ${nextDigit}) to make ${currentDividendPart}.`;
        }
        default:
            return `Put the remainder from the previous step (which is ${previousStep.before}) next to the next digit from the dividend (which is ${nextDigit}) to make ${currentDividendPart}.`;
    }
}

function setupCanvas(
    canvas: HTMLCanvasElement,
    width: number,
    height: number,
): CanvasRenderingContext2D | null {
    const dpr = Math.min(LAYOUT.maxDPR, window.devicePixelRatio || 1);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);

    const context = canvas.getContext('2d');
    if (!context) return null;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.font = `${LAYOUT.fontSize}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace`;
    context.textBaseline = 'alphabetic';

    return context;
}

function drawBusStopFrame(
    context: CanvasRenderingContext2D,
    digitsStartX: number,
    divisionBarY: number,
    digitsBaselineY: number,
    digitCount: number,
    digitWidth: number,
): void {
    context.strokeStyle = COLORS.line;
    context.lineWidth = LAYOUT.lineWidth;

    const barTop = divisionBarY;
    const barBottom = digitsBaselineY + 10;
    context.beginPath();
    context.moveTo(digitsStartX - LAYOUT.barOffset, barTop);
    context.lineTo(digitsStartX - LAYOUT.barOffset, barBottom);
    context.stroke();

    context.beginPath();
    context.moveTo(digitsStartX - LAYOUT.barOffset, divisionBarY);
    context.lineTo(digitsStartX + digitCount * digitWidth + LAYOUT.barOffset, divisionBarY);
    context.stroke();
}

function drawDivisor(
    context: CanvasRenderingContext2D,
    divisorStr: string,
    digitsStartX: number,
    digitsBaselineY: number,
): void {
    context.fillStyle = COLORS.muted;
    context.textAlign = 'right';
    context.fillText(divisorStr, digitsStartX - 10, digitsBaselineY);
}

function drawDividend(
    context: CanvasRenderingContext2D,
    dividendStr: string,
    digitsStartX: number,
    digitsBaselineY: number,
    digitWidth: number,
): void {
    context.fillStyle = COLORS.text;
    context.textAlign = 'left';

    for (let i = 0; i < dividendStr.length; i++) {
        const character = dividendStr[i];
        const x = digitsStartX + i * digitWidth + 4;
        context.fillText(character, x, digitsBaselineY);
    }
}

function drawQuotient(
    context: CanvasRenderingContext2D,
    solution: BusStopSolution,
    digitsStartX: number,
    yQuotient: number,
    digitWidth: number,
    maxColumn: number,
): void {
    context.fillStyle = COLORS.accent;
    context.textAlign = 'center';

    for (let i = 0; i <= maxColumn; i++) {
        const digit = solution.quotientSlots[i];
        if (digit !== undefined) {
            const centerX = digitsStartX + i * digitWidth + digitWidth / 2;
            context.fillText(String(digit), centerX, yQuotient);
        }
    }
}

function drawStrikethroughs(
    context: CanvasRenderingContext2D,
    solution: BusStopSolution,
    steps: BusStopStep[],
    digitsStartX: number,
    digitsBaselineY: number,
    digitWidth: number,
    digitCount: number,
    maxColumn: number,
): void {
    const strikeCols: boolean[] = new Array(digitCount).fill(false);

    for (const step of steps) {
        for (let i = step.startIndex; i <= step.endIndex; i++) {
            strikeCols[i] = true;
        }
    }

    for (let i = 0; i <= maxColumn; i++) {
        if (solution.quotientSlots[i] === 0) {
            strikeCols[i] = true;
        }
    }

    context.strokeStyle = COLORS.danger;
    context.lineWidth = LAYOUT.strikeLineWidth;

    for (let i = 0; i < digitCount; i++) {
        if (!strikeCols[i]) continue;

        const xLeft = digitsStartX + i * digitWidth + 2;
        const xRight = xLeft + (digitWidth - 6);
        const y1 = digitsBaselineY - LAYOUT.fontSize * 0.6;
        const y2 = digitsBaselineY - LAYOUT.fontSize * 0.2;

        context.beginPath();
        context.moveTo(xLeft, y1);
        context.lineTo(xRight, y2);
        context.stroke();
    }
}

function getCarrdigitsBaselineYToShow(
    steps: BusStopStep[],
    upToStep: number | null,
    maxColumn: number,
): number {
    if (upToStep !== null && upToStep >= 0) {
        const currentStep = steps[upToStep];
        return currentStep ? currentStep.endIndex : -1;
    }

    const firstDivideStep = steps.find((s) => s.type === STEP_TYPES.DIVIDE);
    if (!firstDivideStep || firstDivideStep.startIndex !== 0 || firstDivideStep.endIndex < 1) {
        return -1;
    }

    return firstDivideStep.endIndex;
}

function drawEarlyCarries(
    context: CanvasRenderingContext2D,
    solution: BusStopSolution,
    dividendStr: string,
    digitsStartX: number,
    carryBaselineY: number,
    digitWidth: number,
    upToStep: number | null,
    maxColumn: number,
): void {
    if (!solution.steps || solution.steps.length < 1) return;

    const lastCarryIndex = getCarrdigitsBaselineYToShow(solution.steps, upToStep, maxColumn);

    if (lastCarryIndex < 0) return;

    context.font = `${LAYOUT.carryFontSize}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace`;
    context.fillStyle = COLORS.muted;
    context.textAlign = 'right';

    let partialValue = 0;
    for (let i = 0; i < lastCarryIndex; i++) {
        partialValue = partialValue * 10 + Number(dividendStr[i]);
        if (partialValue < solution.divisor) {
            const targetIndex = i + 1;
            const digitLeft = digitsStartX + targetIndex * digitWidth + 4;
            const carryTextX = digitLeft - 2;
            context.fillText(String(partialValue), carryTextX, carryBaselineY);
        }
    }
}

function drawRemainderCarries(
    context: CanvasRenderingContext2D,
    solution: BusStopSolution,
    steps: BusStopStep[],
    digitsStartX: number,
    carryBaselineY: number,
    digitWidth: number,
    digitCount: number,
    upToStep: number | null,
): void {
    if (!solution.steps || solution.steps.length <= 1) return;

    context.font = `${LAYOUT.carryFontSize}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace`;
    context.fillStyle = COLORS.muted;
    context.textAlign = 'right';

    const maxStepIndex =
        upToStep === null
            ? solution.steps.length - 1
            : Math.min(solution.steps.length - 1, upToStep);

    const dividendStr = String(solution.dividend);

    for (let stepIndex = 1; stepIndex <= maxStepIndex; stepIndex++) {
        const currentStep = solution.steps[stepIndex];
        const currentDigitValue = Number(dividendStr[currentStep.endIndex]);

        const carryValue = (currentStep.before - currentDigitValue) / 10;

        if (carryValue === 0 || !Number.isInteger(carryValue)) continue;

        const targetIndex = currentStep.endIndex;
        if (targetIndex >= digitCount) continue;

        const digitLeft = digitsStartX + targetIndex * digitWidth + 4;
        const carryTextX = digitLeft - 2;
        context.fillText(String(carryValue), carryTextX, carryBaselineY);
    }
}

function drawSummary(
    context: CanvasRenderingContext2D,
    solution: BusStopSolution,
    digitsStartX: number,
    digitsBaselineY: number,
    bottomSpace: number,
): void {
    context.fillStyle = COLORS.muted;
    context.textAlign = 'left';

    const summary =
        `${solution.dividend} ÷ ${solution.divisor} = ${solution.quotient}` +
        (solution.remainder ? ` r ${Math.abs(solution.remainder)}` : '');

    context.fillText(summary, digitsStartX, digitsBaselineY + bottomSpace * 0.6);
}

export function draw(
    canvas: HTMLCanvasElement,
    solution: BusStopSolution,
    options: DrawOptions = {},
): void {
    const upToStep = typeof options.upToStep === 'number' ? Math.max(-1, options.upToStep) : null;
    const showSummary = options.showSummary !== false;

    const dividendStr = String(solution.dividend);
    const divisorStr = String(solution.divisor);
    const digitCount = dividendStr.length;

    const topSpace = Math.max(LAYOUT.topSpace, LAYOUT.fontSize * 3.5);
    const widthCSS = LAYOUT.padding * 2 + (digitCount + 6) * LAYOUT.digitWidth;
    const heightCSS = LAYOUT.padding * 2 + topSpace + LAYOUT.bottomSpace + 40;

    const context = setupCanvas(canvas, widthCSS, heightCSS);
    if (!context) return;
    context.fillStyle = COLORS.background;
    context.fillRect(0, 0, widthCSS, heightCSS);

    const digitsStartX = LAYOUT.padding + 40;
    const digitsBaselineY = LAYOUT.padding + topSpace;
    const divisionBarY = digitsBaselineY - (LAYOUT.fontSize + 4);
    const yQuotient = divisionBarY - 8;
    const carryBaselineY = divisionBarY + 8;

    const stepsToDraw =
        upToStep === null ? solution.steps || [] : (solution.steps || []).slice(0, upToStep + 1);
    const maxColumn =
        upToStep === null
            ? digitCount - 1
            : upToStep >= 0
              ? Math.min(digitCount - 1, solution.steps[upToStep].endIndex)
              : -1;

    drawBusStopFrame(
        context,
        digitsStartX,
        divisionBarY,
        digitsBaselineY,
        digitCount,
        LAYOUT.digitWidth,
    );
    drawDivisor(context, divisorStr, digitsStartX, digitsBaselineY);
    drawDividend(context, dividendStr, digitsStartX, digitsBaselineY, LAYOUT.digitWidth);
    drawQuotient(context, solution, digitsStartX, yQuotient, LAYOUT.digitWidth, maxColumn);
    drawStrikethroughs(
        context,
        solution,
        stepsToDraw,
        digitsStartX,
        digitsBaselineY,
        LAYOUT.digitWidth,
        digitCount,
        maxColumn,
    );

    drawEarlyCarries(
        context,
        solution,
        dividendStr,
        digitsStartX,
        carryBaselineY,
        LAYOUT.digitWidth,
        upToStep,
        maxColumn,
    );
    drawRemainderCarries(
        context,
        solution,
        stepsToDraw,
        digitsStartX,
        carryBaselineY,
        LAYOUT.digitWidth,
        digitCount,
        upToStep,
    );

    if (showSummary) {
        context.font = `${LAYOUT.fontSize}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace`;
        drawSummary(context, solution, digitsStartX, digitsBaselineY, LAYOUT.bottomSpace);
    }
}

export function solve(...input: unknown[]): BusStopResult {
    let dividend: number;
    let divisor: number;

    if (input.length === 2) {
        dividend = Number(input[0]);
        divisor = Number(input[1]);
    } else if (input.length === 1 && input[0] && typeof input[0] === 'object') {
        const obj = input[0] as { dividend: unknown; divisor: unknown };
        dividend = Number(obj.dividend);
        divisor = Number(obj.divisor);
    } else if (input.length === 1 && typeof input[0] === 'string') {
        const equation = parseEquation(input[0]);
        if (!equation) {
            return { error: 'Please provide a valid division expression.' };
        }
        dividend = equation.left;
        divisor = equation.right;
    } else {
        return { error: 'Please provide valid numbers for dividend and divisor.' };
    }

    if (Number.isNaN(dividend) || Number.isNaN(divisor)) {
        return { error: 'Please provide valid numbers for dividend and divisor.' };
    }

    return computeLongDivision(dividend, divisor);
}

export const busStop = {
    key: 'busStop',
    name: 'Bus Stop',
    solve,
    draw,
    buildStepText,
    meta: {
        title: 'Bus Stop Method',
        description: 'Solves division questions step-by-step using the Bus Stop method.',
        icon: '÷',
        href: '/methods/bus-stop',
    },
};
